// Import auth utilities
import { checkResponseStatus } from '../utils/authUtils';

// Helper function to get token - avoids circular dependency
const getAuthToken = () => {
  // Try to get from localStorage first (fallback)
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
};

export async function fetchCategories() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  console.log('Making categories request to:', `${API_BASE_URL}api/v1/dashboard/ticket/category`);

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/ticket/category`, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to fetch categories';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON (like HTML error page), get the text
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function createCategory(categoryData) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Use the correct endpoint for creating a category
  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/ticket/category/create`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      name: categoryData.name,
      description: categoryData.description
    }),
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to create category';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function updateCategory(categoryId, categoryData) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Use the correct endpoint and PATCH method for updating a category
  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/ticket/category/update/${categoryId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({
      name: categoryData.name,
      description: categoryData.description
    }),
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to update category';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function deleteCategory(categoryId) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Use the correct endpoint for deleting a category
  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/ticket/category/delete/${categoryId}`, {
    method: 'DELETE',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to delete category';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function createSubcategory(subcategoryData) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Use the correct endpoint for creating a subcategory
  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/ticket/subcategory/create`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      name: subcategoryData.name,
      category_id: subcategoryData.category_id
    }),
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to create subcategory';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function updateSubcategory(subcategoryId, subcategoryData) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Use the correct endpoint and PATCH method for updating a subcategory
  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/ticket/subcategory/update/${subcategoryId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({
      name: subcategoryData.name,
      category_id: subcategoryData.category_id
    }),
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to update subcategory';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function deleteSubcategory(subcategoryId) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Use the correct endpoint for deleting a subcategory
  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/ticket/subcategory/delete/${subcategoryId}`, {
    method: 'DELETE',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to delete subcategory';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function searchCategories(name) {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/';

  if (!API_BASE_URL) {
    throw new Error('API base URL is not configured');
  }

  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/ticket/category/search`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ name })
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Search failed' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  console.log('Categories search result:', data);
  return data;
}
